import React, { useMemo, useState } from 'react';
import BackToTopButton from '@/components/Generic/Buttons/BackToTopButton';
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';
import Pagination from 'components/includes/Pagination';
import { useGetTableDataQuery } from '@/store/kidshub/services/kidshubApi';
import DesktopTable from './desktop/DesktopTable';
import { CountryInvestor, FundLiterature } from 'model/KidsHub';
import usePagination from 'hooks/usePagination';
import { MobileTable } from './mobile/MobileTable';
import Modal from 'components/Modals/Modal';
import PastPerformanceModalContent from './PastPerformanceModalContent';
import PredictivePerformance from './PredictivePerformanceChart/PredictivePerformanceChart';
import { useSitecoreContext } from 'hooks/useSitecoreContext';

type Fields = {
  countryInvestors: Field<CountryInvestor>;
  itemsPerPage: Field<number>;
  anchorId?: string;
};

type Props = {
  fields: Fields;
  rendering: { uid: string };
};

const KidsTable: React.FC<Props> = ({ fields, rendering }) => {
  const {
    sitecoreContext: { language, pageEditing },
  } = useSitecoreContext();

  const [isPastPerformanceModalOpen, setPastPerformanceModal] = useState(false);
  const [isPredictivePerformanceModalOpen, setPredictivePerformanceModalOpen] = useState(false);

  const [selectedRow, setSelectedRow] = useState<FundLiterature | null>(null);
  const openPastPerformance = (row: FundLiterature | null) => {
    if (!!row) setSelectedRow(row);
    setPastPerformanceModal(true);
  };
  const openPredictivePerformance = (row: FundLiterature | null) => {
    if (!!row) setSelectedRow(row);
    setPredictivePerformanceModalOpen(true);
  };

  // Holds the index of the selected share class per row
  const [selectedShareClasses, setSelectedShareClasses] = useState<Record<string, number>>({});
  const { currentPage, setCurrentPage, skip, resultFrom, getResultTo } = usePagination(
    fields.itemsPerPage.value
  );
  const queryParams = useMemo(
    () => ({
      CountryInvestors: fields.countryInvestors.value,
      Skip: skip,
      Take: fields?.itemsPerPage?.value,
      Language: language,
    }),
    [language, fields, skip]
  );
  const { data, isFetching, isLoading } = useGetTableDataQuery(queryParams, { skip: pageEditing });

  const showTable = !!data?.fundLiterature.length;

  const lastPageNumber = (data?.lastPageNumber ?? 0) + 1;

  const onChangePage = (page: number) => {
    if (!isFetching) {
      setCurrentPage(page);
    }
  };
  const onShareClassChange = (row: FundLiterature, shareClassIndex: number) => {
    setSelectedShareClasses((prevState) => ({ ...prevState, [row.id]: shareClassIndex }));
  };

  const selectedRowsShareClass = useMemo(() => {
    const selectedShareClassIndex = selectedShareClasses[selectedRow?.id ?? ''] ?? 0;
    return selectedRow?.shareclasses[selectedShareClassIndex];
  }, [selectedRow?.id, selectedRow?.shareclasses, selectedShareClasses]);

  return (
    <section
      id={fields?.anchorId}
      data-e2e-component-uid={rendering.uid}
      className="container default-section-padding background-white"
    >
      {/* Shown on xl or larger */}
      <DesktopTable
        isLoading={isLoading}
        rows={data?.fundLiterature ?? []}
        selectedShareClasses={selectedShareClasses}
        onShareClassChange={onShareClassChange}
        onTogglePastPerformance={openPastPerformance}
        onTogglePredictivePerformance={openPredictivePerformance}
        className="d-none d-xl-flex"
      />
      {/* Shown on smaller than xl */}
      <MobileTable
        isLoading={isLoading}
        rows={data?.fundLiterature ?? []}
        selectedShareClasses={selectedShareClasses}
        onShareClassChange={onShareClassChange}
        onTogglePastPerformance={openPastPerformance}
        onTogglePredictivePerformance={openPredictivePerformance}
        className="d-xl-none"
      />
      {showTable && (
        <div className={`d-flex-coulmn mb-0 mb-md-1 mb-xl-3 w-100 relative`}>
          <div
            className={`d-flex flex-column-reverse flex-xl-row justify-content-between align-items-center`}
          >
            <p className={`footnote text-grey`}>
              {`Displaying ${resultFrom} - ${getResultTo(data.resultCount)} of ${
                data.resultCount
              } results`}
            </p>
            <BackToTopButton className={`mx-0 my-3 my-lg-4`} />
          </div>
          <Pagination
            isLoading={isFetching}
            showSkeleton={isLoading}
            currentPage={currentPage}
            maxPages={lastPageNumber}
            className={`mt-4 justify-content-center`}
            onSelect={onChangePage}
          />
        </div>
      )}
      <Modal
        renderContent={isPastPerformanceModalOpen}
        isModalOpen={isPastPerformanceModalOpen}
        allowOuterOverflow
        onCloseModal={() => setPastPerformanceModal(false)}
      >
        <PastPerformanceModalContent
          shareClass={selectedRowsShareClass!}
          countryCode={fields.countryInvestors.value.countryCode}
          onCloseModal={() => setPastPerformanceModal(false)}
        />
      </Modal>
      <Modal
        renderContent={isPredictivePerformanceModalOpen}
        isModalOpen={isPredictivePerformanceModalOpen}
        allowOuterOverflow
        onCloseModal={() => setPredictivePerformanceModalOpen(false)}
      >
        <PredictivePerformance
          shareClass={selectedRowsShareClass!}
          countryCode={fields.countryInvestors.value.countryCode}
          onCloseModal={() => setPredictivePerformanceModalOpen(false)}
        />
      </Modal>
    </section>
  );
};

export default KidsTable;
